import { FunctionComponent, useMemo, useState } from 'react';
import { Box, Td, Tr } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SelectField } from '@/components/SelectField';
import {
  calcGoalDiff,
  calcPlayed,
  calcWinRate,
} from '../../utils/calculateStandingDerived';
import { StandingDraft } from '../../types';
import { EditableNumberCell } from './EditableNumberCell';
import { ComputedCell } from './ComputedCell';
import { RowActions } from './RowActions';

export type EligibleTeam = {
  uuid: string;
  name: string;
  acronym?: string;
};

type AddStandingRowProps = {
  eligibleTeams: EligibleTeam[];
  isSaving: boolean;
  onSave: (draft: StandingDraft) => void;
  onCancel: () => void;
  /** Posição que será atribuída pelo backend (apenas visual no input). */
  nextPosition: number;
};

const EMPTY_DRAFT: Omit<StandingDraft, 'team_uuid'> = {
  points: 0,
  wins: 0,
  draws: 0,
  losses: 0,
  goals_for: 0,
  goals_against: 0,
};

const PREFIX = 'pages.application_championship_stage_standings.add_row';

export const AddStandingRow: FunctionComponent<AddStandingRowProps> = ({
  eligibleTeams,
  isSaving,
  onSave,
  onCancel,
  nextPosition,
}) => {
  const { t } = useTranslation();
  const [teamUuid, setTeamUuid] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const played = useMemo(
    () => calcPlayed(draft.wins, draft.draws, draft.losses),
    [draft.wins, draft.draws, draft.losses],
  );
  const goalDiff = useMemo(
    () => calcGoalDiff(draft.goals_for, draft.goals_against),
    [draft.goals_for, draft.goals_against],
  );
  const winRate = useMemo(
    () => calcWinRate(draft.points, played),
    [draft.points, played],
  );

  const canSave = !!teamUuid && !isSaving;

  const setField = (key: keyof typeof EMPTY_DRAFT) => (value: number) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    if (!canSave) return;
    onSave({ team_uuid: teamUuid, ...draft });
  };

  return (
    <Tr bg="blue.50" borderTopWidth="2px" borderTopColor="blue.200">
      <Td px={2} w="32px" />

      <Td px={2} w="48px" textAlign="center">
        <Box
          w="28px"
          h="28px"
          borderRadius="full"
          bg="blue.100"
          color="blue.700"
          fontSize="xs"
          fontWeight="bold"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          {nextPosition}
        </Box>
      </Td>

      <Td px={3} minW="180px">
        <SelectField
          value={teamUuid}
          onChange={(event) => setTeamUuid(event.target.value)}
          size="sm"
          isDisabled={isSaving}
        >
          <option value="">{t(`${PREFIX}.select_team_placeholder`)}</option>
          {eligibleTeams.map((team) => (
            <option key={team.uuid} value={team.uuid}>
              {team.acronym ? `${team.acronym} · ${team.name}` : team.name}
            </option>
          ))}
        </SelectField>
      </Td>

      <Td px={2} w="72px">
        <EditableNumberCell
          value={draft.points}
          isEditing
          isDisabled={isSaving}
          onChange={setField('points')}
          ariaLabel="points"
        />
      </Td>

      <Td px={2} w="56px">
        <ComputedCell value={played} />
      </Td>

      <Td px={2} w="56px">
        <EditableNumberCell
          value={draft.wins}
          isEditing
          isDisabled={isSaving}
          onChange={setField('wins')}
          ariaLabel="wins"
        />
      </Td>

      <Td px={2} w="56px">
        <EditableNumberCell
          value={draft.draws}
          isEditing
          isDisabled={isSaving}
          onChange={setField('draws')}
          ariaLabel="draws"
        />
      </Td>

      <Td px={2} w="56px">
        <EditableNumberCell
          value={draft.losses}
          isEditing
          isDisabled={isSaving}
          onChange={setField('losses')}
          ariaLabel="losses"
        />
      </Td>

      <Td px={2} w="64px">
        <EditableNumberCell
          value={draft.goals_for}
          isEditing
          isDisabled={isSaving}
          onChange={setField('goals_for')}
          ariaLabel="goals_for"
        />
      </Td>

      <Td px={2} w="64px">
        <EditableNumberCell
          value={draft.goals_against}
          isEditing
          isDisabled={isSaving}
          onChange={setField('goals_against')}
          ariaLabel="goals_against"
        />
      </Td>

      <Td px={2} w="64px">
        <ComputedCell value={goalDiff} format="signed" />
      </Td>

      <Td px={2} w="88px">
        <ComputedCell value={winRate} format="percent" />
      </Td>

      <Td px={2} w="88px" textAlign="center">
        <RowActions
          mode={isSaving ? 'saving' : 'edit'}
          hasChanges={canSave}
          onCancel={onCancel}
          onSave={handleSave}
        />
      </Td>
    </Tr>
  );
};
